import { HourlyMetrics, TimeMetricType, ChartDataPoint, PeakHourAnalysis } from './types';

// Color schemes for different metrics
export const TIME_COLOR_SCHEMES = {
  blue: {
    primary: '#3B82F6',
    secondary: '#93C5FD',
    gradient: ['#3B82F6', '#1D4ED8'],
    background: '#EBF8FF',
  },
  green: {
    primary: '#10B981',
    secondary: '#6EE7B7',
    gradient: ['#10B981', '#047857'],
    background: '#ECFDF5',
  },
  red: {
    primary: '#EF4444',
    secondary: '#FCA5A5',
    gradient: ['#EF4444', '#DC2626'],
    background: '#FEF2F2',
  },
  purple: {
    primary: '#8B5CF6',
    secondary: '#C4B5FD',
    gradient: ['#8B5CF6', '#7C3AED'],
    background: '#F5F3FF',
  },
  orange: {
    primary: '#F59E0B',
    secondary: '#FCD34D',
    gradient: ['#F59E0B', '#D97706'],
    background: '#FFFBEB',
  },
  gradient: {
    primary: '#6366F1',
    secondary: '#A78BFA',
    gradient: ['#6366F1', '#8B5CF6', '#EC4899'],
    background: '#F8FAFC',
  },
};

// Default metric colors
export const TIME_METRIC_COLORS: Record<TimeMetricType, keyof typeof TIME_COLOR_SCHEMES> = {
  clicks: 'blue',
  impressions: 'green',
  spend: 'red',
  conversions: 'purple',
  ctr: 'orange',
  cpc: 'blue',
  cpm: 'green',
  cpa: 'red',
  cvr: 'purple',
};

// Get metric value from hourly data
export const getHourlyMetricValue = (hourlyData: HourlyMetrics, metric: TimeMetricType): number => {
  switch (metric) {
    case 'clicks':
      return hourlyData.clicks;
    case 'impressions':
      return hourlyData.impressions;
    case 'spend':
      return hourlyData.spend;
    case 'conversions':
      return hourlyData.conversions;
    case 'ctr':
      return hourlyData.ctr || (hourlyData.impressions > 0 ? (hourlyData.clicks / hourlyData.impressions) * 100 : 0);
    case 'cpc':
      return hourlyData.cpc || (hourlyData.clicks > 0 ? hourlyData.spend / hourlyData.clicks : 0);
    case 'cpm':
      return hourlyData.cpm || (hourlyData.impressions > 0 ? (hourlyData.spend / hourlyData.impressions) * 1000 : 0);
    case 'cpa':
      return hourlyData.cpa || (hourlyData.conversions > 0 ? hourlyData.spend / hourlyData.conversions : 0);
    case 'cvr':
      return hourlyData.cvr || (hourlyData.clicks > 0 ? (hourlyData.conversions / hourlyData.clicks) * 100 : 0);
    default:
      return 0;
  }
};

// Format hour for display
export const formatHour = (hour: number, use24Hour: boolean = true): string => {
  if (use24Hour) {
    return `${hour.toString().padStart(2, '0')}:00`;
  }

  if (hour === 0) return '12:00 AM';
  if (hour === 12) return '12:00 PM';
  if (hour < 12) return `${hour}:00 AM`;
  return `${hour - 12}:00 PM`;
};

// Get period (AM/PM) for 12-hour format
export const getPeriod = (hour: number): 'AM' | 'PM' => {
  return hour < 12 ? 'AM' : 'PM';
};

// Format metric value for display
export const formatTimeMetricValue = (value: number, metric: TimeMetricType): string => {
  switch (metric) {
    case 'spend':
    case 'cpc':
    case 'cpm':
    case 'cpa':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    case 'ctr':
    case 'cvr':
      return `${value.toFixed(2)}%`;
    case 'clicks':
    case 'impressions':
    case 'conversions':
      return new Intl.NumberFormat('en-US').format(Math.round(value));
    default:
      return value.toFixed(2);
  }
};

// Get display name for metric
export const getTimeMetricDisplayName = (metric: TimeMetricType): string => {
  const names: Record<TimeMetricType, string> = {
    clicks: 'Clicks',
    impressions: 'Impressions',
    spend: 'Spend',
    conversions: 'Conversions',
    ctr: 'Click-Through Rate',
    cpc: 'Cost Per Click',
    cpm: 'Cost Per Mille',
    cpa: 'Cost Per Acquisition',
    cvr: 'Conversion Rate',
  };
  return names[metric];
};

// Process hourly data for chart display
export const processHourlyDataForChart = (
  data: HourlyMetrics[],
  metric: TimeMetricType,
  use24Hour: boolean = true
): ChartDataPoint[] => {
  // Ensure we have data for all 24 hours
  const fullDayData: HourlyMetrics[] = [];

  for (let hour = 0; hour < 24; hour++) {
    const existingData = data.find(d => d.hour === hour);
    if (existingData) {
      fullDayData.push(existingData);
    } else {
      // Fill missing hours with zero values
      fullDayData.push({
        hour,
        clicks: 0,
        impressions: 0,
        spend: 0,
        conversions: 0,
      });
    }
  }

  return fullDayData.map(hourData => ({
    hour: hourData.hour,
    value: getHourlyMetricValue(hourData, metric),
    formattedHour: formatHour(hourData.hour, use24Hour),
    period: getPeriod(hourData.hour),
  }));
};

// Analyze peak hours
export const analyzePeakHours = (data: HourlyMetrics[], metric: TimeMetricType): PeakHourAnalysis => {
  const values = data.map(d => ({
    hour: d.hour,
    value: getHourlyMetricValue(d, metric),
  }));

  // Sort by value to find peaks
  const sortedByValue = [...values].sort((a, b) => b.value - a.value);

  const peakHour = sortedByValue[0]?.hour || 0;
  const peakValue = sortedByValue[0]?.value || 0;
  const averageValue = values.reduce((sum, v) => sum + v.value, 0) / values.length;

  // Top 5 hours
  const peakHours = sortedByValue.slice(0, 5).map(v => v.hour);

  // Bottom 5 hours (quiet hours)
  const quietHours = sortedByValue.slice(-5).map(v => v.hour);

  return {
    peakHour,
    peakValue,
    averageValue,
    peakHours,
    quietHours,
  };
};

// Add peak hour indicators to chart data
export const addPeakHourIndicators = (
  chartData: ChartDataPoint[],
  peakAnalysis: PeakHourAnalysis
): ChartDataPoint[] => {
  return chartData.map(point => ({
    ...point,
    isPeakHour: peakAnalysis.peakHours.includes(point.hour),
  }));
};

// Generate time-based insights
export const generateTimeInsights = (data: HourlyMetrics[], metric: TimeMetricType) => {
  const analysis = analyzePeakHours(data, metric);
  const chartData = processHourlyDataForChart(data, metric);

  // Find patterns
  const morningHours = chartData.filter(d => d.hour >= 6 && d.hour <= 11);
  const afternoonHours = chartData.filter(d => d.hour >= 12 && d.hour <= 17);
  const eveningHours = chartData.filter(d => d.hour >= 18 && d.hour <= 23);
  const nightHours = chartData.filter(d => d.hour >= 0 && d.hour <= 5);

  const morningAvg = morningHours.reduce((sum, h) => sum + h.value, 0) / morningHours.length;
  const afternoonAvg = afternoonHours.reduce((sum, h) => sum + h.value, 0) / afternoonHours.length;
  const eveningAvg = eveningHours.reduce((sum, h) => sum + h.value, 0) / eveningHours.length;
  const nightAvg = nightHours.reduce((sum, h) => sum + h.value, 0) / nightHours.length;

  const periods = [
    { name: 'Morning', hours: '6 AM - 11 AM', average: morningAvg },
    { name: 'Afternoon', hours: '12 PM - 5 PM', average: afternoonAvg },
    { name: 'Evening', hours: '6 PM - 11 PM', average: eveningAvg },
    { name: 'Night', hours: '12 AM - 5 AM', average: nightAvg },
  ].sort((a, b) => b.average - a.average);

  return {
    analysis,
    periods,
    bestPeriod: periods[0],
    worstPeriod: periods[periods.length - 1],
  };
};

// Calculate percentage change between hours
export const calculateHourlyChange = (data: HourlyMetrics[], metric: TimeMetricType): ChartDataPoint[] => {
  const chartData = processHourlyDataForChart(data, metric);

  return chartData.map((point, index) => {
    if (index === 0) {
      return { ...point, change: 0 };
    }

    const previousValue = chartData[index - 1].value;
    const change = previousValue > 0 ? ((point.value - previousValue) / previousValue) * 100 : 0;

    return {
      ...point,
      change,
    };
  });
};

// Generate sample hourly data for testing
export const generateSampleHourlyData = (): HourlyMetrics[] => {
  const basePattern = [
    0.2, 0.1, 0.05, 0.05, 0.1, 0.3, 0.6, 0.8, 0.9, 1.0, // 0-9 AM
    0.95, 0.85, 0.9, 0.95, 1.0, 0.9, 0.8, 0.85, 0.9, 0.8, // 10 AM - 7 PM
    0.7, 0.6, 0.4, 0.3, // 8 PM - 11 PM
  ];

  return basePattern.map((multiplier, hour) => {
    const baseClicks = Math.floor(Math.random() * 1000 + 500) * multiplier;
    const baseImpressions = baseClicks * (8 + Math.random() * 4); // 8-12 impression/click ratio
    const baseSpend = baseClicks * (1.5 + Math.random() * 2); // $1.5-3.5 per click
    const baseConversions = baseClicks * (0.02 + Math.random() * 0.03); // 2-5% conversion rate

    return {
      hour,
      clicks: Math.round(baseClicks),
      impressions: Math.round(baseImpressions),
      spend: Math.round(baseSpend * 100) / 100,
      conversions: Math.round(baseConversions),
    };
  });
};
